import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

/* =================================================================== Button */

type Tone = "default" | "primary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
  size?: "sm" | "md" | "lg";
  block?: boolean;
  busy?: boolean;
  icon?: ReactNode;
}

export function Button({
  tone = "default",
  size = "md",
  block,
  busy,
  icon,
  children,
  className = "",
  disabled,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    tone !== "default" ? `btn--${tone}` : "",
    size !== "md" ? `btn--${size}` : "",
    block ? "btn--block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={classes} disabled={disabled || busy} {...rest}>
      {busy ? <span className="spinner" /> : icon}
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  badge?: number;
}

export function IconButton({ label, badge, children, ...rest }: IconButtonProps) {
  return (
    <button className="iconbtn" title={label} aria-label={label} {...rest}>
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="iconbtn__dot">{badge > 99 ? "99+" : badge}</span>
      )}
    </button>
  );
}

/* ===================================================================== Card */

export function Card({
  title,
  subtitle,
  actions,
  flush,
  children,
  footer,
  className = "",
  style,
  ...rest
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
  footer?: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "title">) {
  return (
    <section className={`card ${className}`} style={style} {...rest}>
      {(title || actions) && (
        <header className="card__head">
          <div className="card__title">
            {title}
            {subtitle && <div className="card__sub">{subtitle}</div>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush ? "card__body card__body--flush" : "card__body"}>{children}</div>
      {footer && <footer className="card__foot">{footer}</footer>}
    </section>
  );
}

/* ===================================================================== Pill */

export type PillTone = "neutral" | "red" | "amber" | "green" | "blue" | "violet" | "teal";

export function Pill({
  tone = "neutral",
  round,
  icon,
  children,
}: {
  tone?: PillTone;
  round?: boolean;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={`pill pill--${tone}${round ? " pill--round" : ""}`}>
      {icon}
      {children}
    </span>
  );
}

/**
 * Maps a domain status to a colour. Kept in one place so "Overdue" is the same
 * red on the invoice list, the work board and the portal.
 */
export function statusTone(status: string | null | undefined): PillTone {
  if (!status) return "neutral";

  const key = status.toLowerCase().replace(/[\s_-]/g, "");

  if (
    [
      "done",
      "closed",
      "resolved",
      "paid",
      "approved",
      "confirmed",
      "published",
      "active",
      "completed",
      "delivered",
      "ontrack",
      "healthy",
      "accepted",
      "sent",
      "checkedin",
      "succeeded",
      "won",
      "signed",
    ].includes(key)
  ) {
    return "green";
  }

  if (
    [
      "overdue",
      "breached",
      "failed",
      "cancelled",
      "rejected",
      "declined",
      "critical",
      "blocked",
      "atrisk",
      "lost",
      "revoked",
      "suspended",
      "disabled",
      "deadlettered",
      "urgent",
    ].includes(key)
  ) {
    return "red";
  }

  if (
    [
      "pending",
      "pendingapproval",
      "attention",
      "warning",
      "onhold",
      "waiting",
      "duesoon",
      "partiallypaid",
      "atattention",
      "requested",
      "invited",
      "high",
      "needsreview",
      "revisionrequested",
    ].includes(key)
  ) {
    return "amber";
  }

  if (["inprogress", "inreview", "sending", "processing", "live", "running", "setup"].includes(key)) {
    return "blue";
  }

  if (["draft", "new", "notstarted", "triage", "planning", "lead"].includes(key)) {
    return "neutral";
  }

  return "violet";
}

export function priorityTone(priority: string | null | undefined): PillTone {
  switch ((priority ?? "").toLowerCase()) {
    case "critical":
      return "red";
    case "high":
      return "red";
    case "medium":
      return "amber";
    case "low":
      return "neutral";
    default:
      return "neutral";
  }
}

/* ================================================================== Avatars */

export function Avatar({
  name,
  src,
  size = "md",
}: {
  name: string | null | undefined;
  src?: string | null;
  size?: "sm" | "md" | "lg";
}) {
  const text = (name ?? "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("");

  return (
    <span className={`avatar avatar--${size}`} title={name ?? undefined}>
      {src ? <img src={src} alt="" /> : text || "?"}
    </span>
  );
}

/* =================================================================== Fields */

interface FieldShellProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  /** Receives the control's id, whether it is invalid, and the id of the hint or error that describes it. */
  children: (id: string, invalid: boolean, describedBy: string | undefined) => ReactNode;
}

/**
 * A labelled form field.
 *
 * The label names the control and nothing else. The hint or error is its own element, which the control
 * points at with aria-describedby, so a screen reader announces the name and then the help instead of
 * reading both as one long name.
 */
export function Field({ label, required, hint, error, children }: FieldShellProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const describedBy = error || hint ? noteId : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <em aria-hidden>*</em>}
      </label>
      {children(id, Boolean(error), describedBy)}
      {error ? (
        <span id={noteId} className="field__error">
          {error}
        </span>
      ) : hint ? (
        <span id={noteId} className="field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** The ids a control is described by: the field's own note first, then any the caller passed. */
function describedByIds(...ids: (string | undefined)[]): string | undefined {
  return ids.filter(Boolean).join(" ") || undefined;
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({
  label,
  hint,
  error,
  required,
  "aria-describedby": describedByProp,
  ...rest
}: TextFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id, invalid, describedBy) => (
        <input
          id={id}
          className={`input${invalid ? " input--invalid" : ""}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedByIds(describedBy, describedByProp)}
          required={required}
          {...rest}
        />
      )}
    </Field>
  );
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  label: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function SelectField({
  label,
  hint,
  error,
  options,
  placeholder,
  required,
  "aria-describedby": describedByProp,
  ...rest
}: SelectFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id, invalid, describedBy) => (
        <select
          id={id}
          className={`select${invalid ? " select--invalid" : ""}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedByIds(describedBy, describedByProp)}
          required={required}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  "aria-describedby": describedByProp,
  ...rest
}: TextAreaFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id, invalid, describedBy) => (
        <textarea
          id={id}
          className={`textarea${invalid ? " textarea--invalid" : ""}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedByIds(describedBy, describedByProp)}
          required={required}
          {...rest}
        />
      )}
    </Field>
  );
}

export function Checkbox({
  label,
  ...rest
}: { label: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export function Toggle({
  label,
  ...rest
}: { label?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="check">
      <input type="checkbox" className="switch" {...rest} />
      {label && <span>{label}</span>}
    </label>
  );
}

/* ===================================================================== Tabs */

export interface TabDef {
  key: string;
  label: string;
  icon?: ReactNode;
  hidden?: boolean;
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
}) {
  const visible = tabs.filter((t) => !t.hidden);

  return (
    <div className="tabs" role="tablist">
      {visible.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          type="button"
          aria-selected={tab.key === active}
          className={`tab${tab.key === active ? " tab--active" : ""}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* =================================================================== States */

export function EmptyState({
  title,
  message,
  action,
  icon,
}: {
  title: string;
  message?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="state">
      {icon ?? <Icon.list />}
      <b>{title}</b>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const message =
    error instanceof Error ? error.message : "Something went wrong loading this.";

  return (
    <div className="state state--error">
      <Icon.alert />
      <b>This could not be loaded</b>
      <p>{message}</p>
      {onRetry && (
        <Button size="sm" icon={<Icon.refresh />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function LoadingRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div style={{ padding: 14 }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: "flex", gap: 12, marginBottom: 11 }}>
          {Array.from({ length: columns }).map((_, c) => (
            <div
              key={c}
              className="skeleton"
              style={{ height: 13, flex: c === 0 ? 2 : 1 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ================================================================= Progress */

/**
 * A progress bar.
 *
 * `label` is required rather than optional: a progressbar with no accessible
 * name is announced as "progress bar, 75 percent" with no indication of what is
 * 75 percent done, which is no use to anybody relying on a screen reader.
 * Pass `labelledBy` instead when visible text already names it.
 */
export function Progress({
  value,
  tone,
  label,
  labelledBy,
}: {
  value: number;
  tone?: "red" | "green" | "amber" | "blue";
  label?: string;
  labelledBy?: string;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const auto = pct >= 85 ? "green" : pct >= 60 ? "amber" : "red";
  const resolved = tone ?? auto;

  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`${pct}%`}
      aria-label={labelledBy ? undefined : (label ?? "Progress")}
      aria-labelledby={labelledBy}
    >
      <div
        className={`progress__fill${resolved === "red" ? "" : ` progress__fill--${resolved}`}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ==================================================================== Modal */

export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // The latest close handler, read when Escape is pressed. Callers often pass an inline function;
  // depending on it would re-run the effect below on every keystroke and pull focus out of the
  // field being typed in.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  // Once, when the dialog opens: Escape closes, the page underneath stops scrolling, and focus
  // moves to the first field (or, in a confirmation without fields, the first action) so a
  // keyboard user starts inside the dialog. Focus goes back to where it was when the dialog closes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = ref.current;

    document.addEventListener("keydown", onKey);
    (
      dialog?.querySelector<HTMLElement>(
        ".modal__body input:not([type=hidden]):not([disabled]), .modal__body select:not([disabled]), .modal__body textarea:not([disabled])",
      ) ??
      dialog?.querySelector<HTMLElement>(".modal__body button, .modal__foot button") ??
      dialog?.querySelector<HTMLElement>("button")
    )?.focus();

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return createPortal(
    <div
      className="scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`modal${wide ? " modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
      >
        <header className="modal__head">
          <div style={{ flex: 1 }}>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <Icon.x width={16} height={16} />
          </IconButton>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/* =================================================================== Toasts */

export interface ToastMessage {
  id: number;
  tone: "ok" | "err" | "info";
  title: string;
  detail?: string;
  /** One follow-up offered with the message, such as undoing what was just done. */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

let pushToast: ((toast: Omit<ToastMessage, "id">) => void) | null = null;

export function toast(
  tone: ToastMessage["tone"],
  title: string,
  detail?: string,
  action?: ToastAction,
): void {
  pushToast?.({ tone, title, detail, action });
}

/** Reports a failed call in the user's terms, including validation detail. */
export function toastError(error: unknown, fallback = "That did not work"): void {
  const message = error instanceof Error ? error.message : String(error);
  toast("err", fallback, message);
}

export function ToastHost() {
  const [items, setItems] = useState<ToastMessage[]>([]);

  useEffect(() => {
    let nextId = 1;

    pushToast = (toastToAdd) => {
      const id = nextId++;
      setItems((current) => [...current, { ...toastToAdd, id }]);
      // A message with an action stays long enough to act on.
      setTimeout(
        () => setItems((current) => current.filter((t) => t.id !== id)),
        toastToAdd.action ? 10_000 : 6000,
      );
    };

    return () => {
      pushToast = null;
    };
  }, []);

  if (items.length === 0) return null;

  return createPortal(
    <div className="toasts" role="status" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`toast toast--${item.tone}`}>
          {item.tone === "ok" ? (
            <Icon.check />
          ) : item.tone === "err" ? (
            <Icon.alert />
          ) : (
            <Icon.bell />
          )}
          <div style={{ minWidth: 0 }}>
            <b>{item.title}</b>
            {item.detail && <p>{item.detail}</p>}
          </div>
          {item.action && (
            <button
              type="button"
              className="btn btn--sm"
              style={{ marginInlineStart: "auto", alignSelf: "center", flex: "none" }}
              onClick={() => {
                item.action?.onClick();
                setItems((current) => current.filter((t) => t.id !== item.id));
              }}
            >
              {item.action.label}
            </button>
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}
